/**
 * Per-browser "active tenant" pin.
 *
 * When a user holds tenant_admin (or participant) memberships in
 * multiple tenants we previously picked whichever the API returned
 * first — fine for the common single-tenant case, confusing the
 * moment someone admins two organisations.
 *
 * Concrete reported case: an existing tenant_admin in Tenant A
 * accepted an invitation to Tenant B in the same browser session.
 * `activeAdminTenantId` still returned Tenant A, so the post-accept
 * Dashboard rendered Tenant A's data and the operator wondered why
 * Tenant B was missing.
 *
 * This module persists a user's preferred tenant in localStorage.
 * `activeAdminTenantId` and `activeParticipantMembership` consult
 * the pin first and fall back to "first matching membership" when
 * the pin is empty or stale (membership gone / role changed). It's
 * intentionally simple — when we add a real topbar tenant switcher
 * that surface will set + clear the pin too.
 */

const KEY = 'tenewtons.pinnedTenantId';

export function pinTenant(tenantId: string | null): void {
  try {
    if (tenantId === null) {
      localStorage.removeItem(KEY);
    } else {
      localStorage.setItem(KEY, tenantId);
    }
  } catch {
    // localStorage can throw under private-browsing / quota-exceeded.
    // The pin is a UX nicety, not load-bearing, so we swallow it.
  }
}

export function readPinnedTenant(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}
